import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react';

type Announce = (text: string) => void;

const AnnounceContext = createContext<Announce | null>(null);

/**
 * Mounts the one polite live region; UiProvider renders it once at the app root.
 * Two announce calls within 50ms leave only the second message: each call
 * cancels the pending one.
 */
export function Announcer({ children }: { children?: ReactNode }): ReactElement {
  const [message, setMessage] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const announce = useCallback<Announce>((text) => {
    // Clear first so an identical message is announced again.
    setMessage('');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(text), 50);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="absolute m-0 p-0 w-px h-px overflow-hidden border-0 bg-transparent"
      >
        {message}
      </div>
    </AnnounceContext.Provider>
  );
}

/** Returns announce(text); an optional default text is used when called with no argument. */
export function useAnnounce(defaultText?: string): (text?: string) => void {
  const announce = useContext(AnnounceContext);
  if (!announce) throw new Error('useAnnounce must be used inside Announcer');
  return useCallback((text) => announce(text ?? defaultText ?? ''), [announce, defaultText]);
}
